import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppTopBar } from "@/components/AppTopBar";

const idr = (n) => (n == null ? "-" : new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(Number(n)));
const PILL = { READY: "on", ALREADY_INITIALIZED: "off", UNKNOWN_SKU: "off", INVALID_COST: "off", NO_STOCK: "off", COMPONENT_DERIVED: "off", DUPLICATE_ROW: "off", NOT_ELIGIBLE: "off", VALID: "on", MISSING_OPENING_HPP: "off", INVALID_QTY: "off", ALREADY_HAS_STOCK: "off", COMPONENT_DERIVED_NOT_ELIGIBLE: "off" };

export default function BulkOpeningHPP() {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [pv, setPv] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [osFile, setOsFile] = useState(null);
  const [osPv, setOsPv] = useState(null);

  if (user && user.role !== "OWNER") {
    return (<div className="app-shell"><AppTopBar active="opening-hpp" /><main className="content"><div className="alert-error" data-testid="access-denied">Akses ditolak. OWNER only.</div></main></div>);
  }

  const downloadTemplate = async () => {
    setError("");
    try {
      const res = await api.get("/inventory/opening-hpp/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "template_hpp_awal.xlsx"; a.click(); URL.revokeObjectURL(url);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const doPreview = async () => {
    setError(""); setNotice(""); setPv(null);
    if (!file) { setError("Pilih file .xlsx (kolom SKU | HPP_AWAL)."); return; }
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/inventory/opening-hpp/preview", fd); setPv(data); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const doCommit = async () => {
    setError(""); setNotice("");
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/inventory/opening-hpp/commit", fd);
      setNotice(`Inisialisasi HPP awal: ${data.initialized} SKU. Dilewati: ${JSON.stringify(data.skipped)}`);
      setPv(null); setFile(null);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  // ---- Bulk Opening Stock / Qty Awal ----
  const osDownloadTemplate = async () => {
    setError("");
    try {
      const res = await api.get("/inventory/opening-stock/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "template_opening_stock.xlsx"; a.click(); URL.revokeObjectURL(url);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const osPreview = async () => {
    setError(""); setNotice(""); setOsPv(null);
    if (!osFile) { setError("Pilih file (kolom SKU | OPENING_QTY)."); return; }
    const fd = new FormData(); fd.append("file", osFile);
    try { const { data } = await api.post("/inventory/opening-stock/preview", fd); setOsPv(data); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const osCommit = async () => {
    setError(""); setNotice("");
    const fd = new FormData(); fd.append("file", osFile);
    try {
      const { data } = await api.post("/inventory/opening-stock/commit", fd);
      setNotice(`Opening stock diset: ${data.applied} SKU. Dilewati: ${JSON.stringify(data.skipped)}`);
      setOsPv(null); setOsFile(null);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="app-shell">
      <AppTopBar active="opening-hpp" />
      <main className="content">
        {error && <div className="alert-error" data-testid="error-banner">{error}</div>}
        {notice && <div className="alert-ok" data-testid="notice-banner">{notice}</div>}

        <section className="panel" data-testid="hpp-panel">
          <h2 className="panel-title">Bulk Setup HPP Awal Inventory</h2>
          <p className="hint">Set biaya/HPP awal per unit untuk SKU cutover yang stoknya ada tetapi cost basis belum diinisialisasi (UNINITIALIZED). Bukan alat repricing — SKU yang sudah berjalan akan ditolak (ALREADY_INITIALIZED). Tidak mengubah qty, histori, atau keuangan.</p>
          <p className="hint" data-testid="hpp-instruction">HPP Awal diisi per unit dasar stok (BASE UOM). Contoh: jika BASE UOM = PCS dan HPP Rp27.000/lusin, isi Rp2.250 per PCS. Kolom BASE_UOM di template hanya referensi kanonik (read-only) dan tidak mengubah konfigurasi Produk/UOM.</p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
            <button className="btn-ghost" onClick={downloadTemplate} data-testid="hpp-template-btn">Download Template</button>
            <div><label className="field-label">File (.xlsx: SKU | BASE_UOM | HPP_AWAL_PER_BASE_UNIT)</label>
              <input className="field-input" type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files[0])} data-testid="hpp-file" /></div>
            <button className="btn-primary" onClick={doPreview} disabled={!file} data-testid="hpp-preview-btn">Preview</button>
          </div>
        </section>

        {pv && (
          <section className="panel" data-testid="hpp-preview">
            <h3 className="panel-title">Preview ({pv.ready} READY / {pv.total} baris)</h3>
            <div className="table-scroll">
              <table className="data-table" data-testid="hpp-table">
                <thead><tr><th>SKU</th><th>Base UOM</th><th>Qty Stok</th><th>Cost Status</th><th>HPP Saat Ini / Base Unit</th><th>HPP Awal Baru / Base Unit</th><th>Nilai Inventory Hasil</th><th>Status</th></tr></thead>
                <tbody>
                  {pv.rows.map((r, i) => (
                    <tr key={i} data-testid={`hpp-row-${r.sku_code}`}>
                      <td className="mono">{r.sku_code}</td>
                      <td className="mono" data-testid={`hpp-baseuom-${r.sku_code}`}>{r.base_uom ?? "-"}</td>
                      <td className="mono">{r.current_qty ?? "-"}</td>
                      <td>{r.current_cost_status ?? "-"}</td>
                      <td className="mono">{r.current_avg_cost != null ? idr(r.current_avg_cost) : "-"}</td>
                      <td className="mono">{r.hpp_awal}</td>
                      <td className="mono">{r.resulting_value != null ? idr(r.resulting_value) : "-"}</td>
                      <td><span className={`status-pill ${PILL[r.status] || "off"}`} data-testid={`hpp-status-${r.sku_code}`}>{r.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="form-actions">
              <button className="btn-primary" onClick={doCommit} disabled={pv.ready === 0} data-testid="hpp-commit-btn">Confirm & Set HPP Awal ({pv.ready} READY)</button>
            </div>
          </section>
        )}

        <section className="panel" data-testid="os-panel">
          <h2 className="panel-title">Bulk Opening Stock / Qty Awal</h2>
          <p className="hint">Seed qty GOOD awal untuk banyak SKU saat cutover. Kolom file: SKU | OPENING_QTY. HPP tidak diisi ulang — nilai = OPENING_QTY × HPP Awal (opening_unit_cost) yang sudah tersimpan. Hanya untuk SKU fisik yang masih dalam status opening (belum pernah punya stok/histori). Tidak membuat transaksi kas/keuangan.</p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
            <button className="btn-ghost" onClick={osDownloadTemplate} data-testid="os-template-btn">Download Template</button>
            <div><label className="field-label">File (.xlsx/.csv: SKU | OPENING_QTY)</label>
              <input className="field-input" type="file" accept=".xlsx,.csv" onChange={(e) => { setOsFile(e.target.files[0]); setOsPv(null); }} data-testid="os-file" /></div>
            <button className="btn-primary" onClick={osPreview} disabled={!osFile} data-testid="os-preview-btn">Preview</button>
          </div>
        </section>

        {osPv && (
          <section className="panel" data-testid="os-preview">
            <h3 className="panel-title">Preview Opening Stock ({osPv.valid} VALID / {osPv.total} baris)</h3>
            <div className="hint" data-testid="os-summary">
              {Object.entries(osPv.summary).map(([k, v]) => (
                <span key={k} style={{ marginRight: 14 }}><b>{k}</b>: {v}</span>
              ))}
            </div>
            <div className="table-scroll">
              <table className="data-table" data-testid="os-table">
                <thead><tr><th>SKU</th><th>Opening Qty</th><th>HPP Awal</th><th>Nilai Opening</th><th>Status</th><th>Reason</th></tr></thead>
                <tbody>
                  {osPv.rows.map((r, i) => (
                    <tr key={i} data-testid={`os-row-${r.sku_code}`}>
                      <td className="mono">{r.sku_code}</td>
                      <td className="mono">{r.opening_qty ?? "-"}</td>
                      <td className="mono">{r.opening_unit_hpp != null ? idr(r.opening_unit_hpp) : "-"}</td>
                      <td className="mono">{r.opening_value != null ? idr(r.opening_value) : "-"}</td>
                      <td><span className={`status-pill ${PILL[r.status] || "off"}`} data-testid={`os-status-${r.sku_code}`}>{r.status}</span></td>
                      <td className="hint">{r.reason || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="form-actions">
              <button className="btn-primary" onClick={osCommit} disabled={!osPv.valid} data-testid="os-commit-btn">Commit Opening Stock ({osPv.valid} VALID)</button>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
